import {
  isMeetingSessionRoleGreaterThan,
  isMeetingSessionRoleGreaterThanOrEqual,
  isRoleGreaterThanOrEqual,
  MeetingSessionOvenMediaReceiverManagerBaseData,
} from "@kalyzee/kast-web-sdk";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import {
  Meeting,
  MeetingRoom,
  MeetingRoomAudienceType,
  MeetingRoomHighlightingState,
  MeetingRoomStatus,
  MeetingRoomType,
  MeetingSession,
  MeetingSessionHighlightingState,
  MeetingSessionRole,
  MeetingSessionStatus,
  UserRole,
} from "@kalyzee/kast-web-sdk/websocket";
import { useMemo } from "react";
import {
  ActivityData,
  ExpertView,
  PedagogyView,
  PopulatedMeetingData,
  PopulatedMeetingRoom,
  PopulatedMeetingRooms,
  PopulatedMeetingSession,
  PopulatedMeetingUser,
  SessionEnabledStreamsData,
  StudentView,
} from "../interfaces/populatedData";
import { useMeetingSelector } from "./meeting";
const _createPopulatedUsers = (
  meeting?: Meeting,
  ownSession?: MeetingSession,
  receptionSessions?: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>[],
  excludeOwnSession?: boolean
):
  | {
      me: PopulatedMeetingUser;
      users: PopulatedMeetingUser[];
      studentUsers: PopulatedMeetingUser[];
      expertUsers: PopulatedMeetingUser[];
    }
  | undefined => {
  if (!meeting || !ownSession?.user) return undefined;

  const userMap = new Map<string, PopulatedMeetingUser>();
  const sessionMap = new Map<string, PopulatedMeetingSession>();

  meeting.actors?.forEach((actor) => {
    const { user } = actor;
    if (!user) return;
    const populatedUser: PopulatedMeetingUser = {
      id: user.id,
      user,
      actor,
      sessions: [],
      active: false,
    };
    userMap.set(populatedUser.id, populatedUser);
  });

  meeting.sessions?.forEach((session) => {
    const user = session.user;
    if (!user) return;
    let populatedUser = userMap.get(user.id);
    if (!populatedUser) {
      // User without actor
      populatedUser = {
        id: user.id,
        user,
        sessions: [],
        active: false,
      };
      userMap.set(populatedUser.id, populatedUser);
    }
    if (session.status === MeetingSessionStatus.RUNNING) {
      populatedUser.active = true;
    }
    if (session.id === ownSession.id && excludeOwnSession) {
      return;
    }
    const populatedSession: PopulatedMeetingSession = {
      id: session.id,
      session,
      videoSessions: [],
      user: populatedUser,
    };
    sessionMap.set(populatedSession.id, populatedSession);
    populatedUser.sessions.push(populatedSession);
  });

  meeting.usersData?.forEach((userData) => {
    const populatedUser = userMap.get(userData.userId);
    if (!populatedUser) return;
    populatedUser.data = userData;
  });

  (receptionSessions ?? []).forEach((session) => {
    const sessionId = session.data.session.id;
    const aggregatedSession = sessionMap.get(sessionId);
    if (!aggregatedSession) return;
    aggregatedSession.videoSessions.push(session);
  });

  const studentUsers: PopulatedMeetingUser[] = [];
  const expertUsers: PopulatedMeetingUser[] = [];

  userMap.forEach((user) => {
    if (isRoleGreaterThanOrEqual(user.user.role, UserRole.ORGANIZER)) {
      expertUsers.push(user);
    } else {
      studentUsers.push(user);
    }
  });

  let me = userMap.get(ownSession.user.id);
  if (!me) {
    // My user was not found in actors and sessions
    me = {
      id: ownSession.user.id,
      user: ownSession.user,
      sessions: [],
      active: false,
    };
  }

  return {
    me,
    users: Array.from(userMap.values()),
    studentUsers,
    expertUsers,
  };
};

const _createAndInjectPopulatedRooms = (rooms: MeetingRoom[], users: PopulatedMeetingUser[]): PopulatedMeetingRooms => {
  const groups: PopulatedMeetingRoom[] = [];
  const waitingRooms: PopulatedMeetingRoom[] = [];
  let activityRoom: PopulatedMeetingRoom | undefined;

  rooms.forEach((room) => {
    const pUsers = users.filter((user) => (room.enlistedUserIds ?? []).includes(user.id));

    const pRoom: PopulatedMeetingRoom = {
      id: room.id,
      room,
      users: pUsers,
    };

    if (room.type === MeetingRoomType.Group) {
      pUsers.forEach((user) => (user.group = pRoom));
      groups.push(pRoom);
    } else if (room.type === MeetingRoomType.WaitingRoom) {
      waitingRooms.push(pRoom);
    } else {
      activityRoom = pRoom;
    }
  });

  return { groups, waitingRooms, activityRoom };
};

export const usePopulatedMeetingData = (meetingContainer?: MeetingContainerAPI, excludeOwnSession?: boolean): PopulatedMeetingData | undefined => {
  const [name] = useMeetingSelector(meetingContainer, (state) => state.meeting?.name);
  const [startedAt] = useMeetingSelector(meetingContainer, (state) => state.meeting?.startedAt);
  const [isPedagogy] = useMeetingSelector(meetingContainer, (state) => state.meeting?.specializedData?.isPedagogy);
  const [meeting] = useMeetingSelector(meetingContainer, (state) => state.meeting);
  const [ownSession] = useMeetingSelector(meetingContainer, (state) => state.session);
  const [receptionSessions] = useMeetingSelector(meetingContainer, (state) => state.receptionSessions);
  const [rooms] = useMeetingSelector(meetingContainer, (state) => state.rooms);

  const populatedData = useMemo(() => {
    if (!rooms || !name) return undefined;

    const populatedUsers = _createPopulatedUsers(meeting, ownSession, receptionSessions, excludeOwnSession);
    if (!populatedUsers) return undefined;
    const { me, users, expertUsers, studentUsers } = populatedUsers;

    const populatedRooms = _createAndInjectPopulatedRooms(rooms, users);

    return { name, startedAt: startedAt ? new Date(startedAt) : undefined, isPedagogy: isPedagogy!!, me, users, expertUsers, studentUsers, rooms: populatedRooms };
  }, [isPedagogy, meeting, ownSession, receptionSessions, rooms, excludeOwnSession]);

  // TODO-V4 Clean this
  (window as any).populatedData = populatedData;

  return populatedData;
};

// Activity DATA

export const useActivityData = (populatedMeetingData: PopulatedMeetingData | undefined) =>
  useMemo((): ActivityData | undefined => {
    if (!populatedMeetingData) return undefined;

    const { rooms, users } = populatedMeetingData;

    if (!rooms || !users) return undefined;

    const { groups, activityRoom } = rooms;

    if (!activityRoom) return undefined;
    const { audience, enlistedUserIds } = activityRoom.room;

    if (activityRoom.room.status === MeetingRoomStatus.Closed) return undefined;

    let outputData: ActivityData | undefined = undefined;

    // 1 - we collect the users who respect the constraint

    if (audience?.type === MeetingRoomAudienceType.RoomMember) {
      if (audience.values === "*") return undefined; // not supported for now
      const visibleGroupId = audience.values.length ? audience.values[0] : undefined;
      if (!visibleGroupId) return undefined;
      const group = groups.find((g) => g.id === visibleGroupId);
      if (group && group.room.status === MeetingRoomStatus.Ongoing) {
        outputData = {
          mode: "one_group",
          group,
          sessions: group.users.flatMap((user) => user.sessions),
          confirmedSessions: [],
          requiresConfirmation: false,
        };
      }
    } else if (audience?.type === MeetingRoomAudienceType.RoomLeader) {
      const leaders: PopulatedMeetingUser[] = [];
      groups.forEach((group) => {
        if (group.room.status !== MeetingRoomStatus.Ongoing || (audience.values !== "*" && !audience.values.includes(group.id))) return;
        const leaderId = group.room.leaderUserId;
        if (leaderId) {
          const leader = group.users.find((u) => u.id === leaderId);
          if (leader) {
            leaders.push(leader);
          }
        }
      });
      if (audience.values === "*") {
        outputData = {
          mode: "all_leaders",
          sessions: leaders.flatMap((user) => user.sessions),
          confirmedSessions: [],
          requiresConfirmation: true,
        };
      } else {
        outputData = {
          mode: "one_leader",
          group: leaders.length ? leaders[0].group : undefined,
          sessions: leaders.length ? leaders[0].sessions : [], // currently, it's either all or one leader
          confirmedSessions: [],
          requiresConfirmation: true,
        };
      }
    } else if (audience?.type === MeetingRoomAudienceType.HighlightedUser) {
      if (audience.values !== "*") return undefined; // not supported
      const highlightedSessions: PopulatedMeetingSession[] = [];
      // Some users are highlighted thro the room
      groups.forEach((group) => {
        if (group.room.status === MeetingRoomStatus.Ongoing && group.room.highlighting === MeetingRoomHighlightingState.ONGOING) {
          highlightedSessions.push(...group.users.flatMap((user) => user.sessions));
        }
      });
      // Some users are highlighted thro the session
      users.forEach((user) => {
        user.sessions.forEach((session) => {
          if (session.session.highlighting === MeetingSessionHighlightingState.ONGOING) {
            highlightedSessions.push(session);
          }
        });
      });
      outputData = {
        mode: "highlight",
        sessions: highlightedSessions,
        confirmedSessions: [],
        requiresConfirmation: true,
      };
    } else if (audience?.type === MeetingRoomAudienceType.Custom) {
      // TODO-V4 : Temporary HACK
      // This audience does not target any user, it's for the pedagogy expert to talk with experts
      // This will be ultimately replaced by a multi-activity system
      // the peda experts will enlist themselves in it to let the experts know they are here

      // When the peda creates the activity, he enlists himself
      // When the peda is not in the meeting, the activity is not considered,
      // it's important because the experts are not allowed to close the activity, only the peda can,
      // so we could fall in cases where the peda just leaves and the activity closing fails,
      // leaving the experts jailed here

      // when an other peda comes, he adds himself to the activity room,
      // so that we can keep track of peda users ...

      // With The multi-activity system, we won'y have such a problem anymore because everyone
      // will be free to leave/join
      if (
        !activityRoom.users.flatMap((u) => u.sessions).filter((s) => isMeetingSessionRoleGreaterThan(s.session.role, MeetingSessionRole.SUPERSPECTATOR)).length
      )
        return undefined;

      outputData = {
        mode: "pedagogy",
        sessions: [],
        confirmedSessions: [],
        requiresConfirmation: false,
      };
    }

    if (!outputData) return undefined;

    // 2 - we remove the users who did not enlist yet (confirmed on the navigation popup)

    if (outputData.requiresConfirmation) {
      outputData.confirmedSessions = outputData.sessions.filter((session) => enlistedUserIds?.includes(session.user.id));
    } else {
      outputData.confirmedSessions = outputData.sessions;
    }

    return outputData;
  }, [populatedMeetingData]);

// Broadcasting expert sessions

export const useBroadcastingExpertSessions = (populatedData: PopulatedMeetingData | undefined): PopulatedMeetingSession[] =>
  useMemo<PopulatedMeetingSession[]>(() => {
    if (!populatedData) return [];

    const { expertUsers } = populatedData;

    const sessions = expertUsers.flatMap((expert) => expert.sessions);

    return sessions.filter((session) => session.session.canBeHeardByEveryone);
  }, [populatedData]);

// Available views

export const useStudentAvailableViews = (
  populatedData: PopulatedMeetingData | undefined,
  activityData: ActivityData | undefined,
  userId: string | undefined,
  sessionId: string | undefined
): StudentView[] =>
  useMemo<StudentView[]>(() => {
    if (!populatedData || !userId) return [];

    const { me, isPedagogy, rooms, studentUsers, expertUsers } = populatedData;

    const views: StudentView[] = [];

    // (window as any).views = views;

    let type: StudentView["type"] = "normal";
    let students: SessionEnabledStreamsData[] = [];
    let experts: SessionEnabledStreamsData[] = [];

    const selfGroup = rooms.groups.find((group) => (group.room.enlistedUserIds ?? []).includes(userId) && group.room.status !== MeetingRoomStatus.Closed);

    const selfSession = me.sessions.find((session) => session.id === sessionId);

    const _pedagogy =
      // If the current meeting is pedagogy
      isPedagogy ||
      // My session is not running, i belong in pedagogy
      selfSession?.session.status !== MeetingSessionStatus.RUNNING;

    if (_pedagogy) {
      // If i am here, then i am in a peda group
      // (see useMultiMeetingAPI hook)
      type = "pedagogy_room";
      if (selfGroup) {
        // peda room contains both users and peda
        selfGroup.users
          .flatMap((u) => u.sessions)
          .forEach((session) => {
            if (isMeetingSessionRoleGreaterThanOrEqual(session.session.role, MeetingSessionRole.ADMINISTRATOR)) {
              experts.push({ session, audio: true, video: true });
            } else {
              students.push({ session, audio: true, video: true });
            }
          });
      }
      views.push({ type, students, experts, selfGroup });
      return views;
    }

    if (
      activityData?.mode === "highlight"
      // There is at least one online highlighted user (even if not confirmed)
      // Now the activity room is closed on backend automatically when no one is highlighted anymore
      // && activityData.sessions.length
    ) {
      const areGroupsActive = rooms.groups.some((group) => group.room.status !== MeetingRoomStatus.Closed);
      // Highlight view is available
      views.push({
        type: "highlight",
        students: studentUsers.flatMap((student) => student.sessions).map((s) => {
          const isConfirmed = activityData.confirmedSessions.some(cs => cs.id === s.id);
          return { 
            session: s, 
            audio: isConfirmed || !areGroupsActive, // we can hear all students, except in group mode
            video: isConfirmed,
          };
        }),
        experts: expertUsers.flatMap((expert) => expert.sessions).map((s) => ({ session: s, audio: true, video: true })),
      });
      if (activityData.sessions.some((session) => session.id === sessionId)) {
        return views; // If i am highlighted, then only this view is available
      }
    }

    const areGroupsOngoing = rooms.groups.some((group) => group.room.status === MeetingRoomStatus.Ongoing);
    const areGroupsActive = rooms.groups.some((group) => group.room.status !== MeetingRoomStatus.Closed);

    if (areGroupsOngoing) {
      type = "group";
      if (selfGroup) {
        // For now, all activities involve group
        if (activityData) {
          // There is an going activity, we need to check if i'm in it
          if (activityData.confirmedSessions.some((session) => session.user.id === userId)) {
            // I am in the activity, i see the activity students and experts
            students = activityData.confirmedSessions.map((s) => ({ session: s, audio: true, video: true }));
            experts = expertUsers.flatMap((expert) => expert.sessions).map((s) => ({ session: s, audio: true, video: true }));
            if (activityData.mode === "one_group") {
              type = "activity_one_group";
            } else if (activityData.mode === "all_leaders") {
              type = "activity_all_leaders";
            } else if (activityData.mode === "one_leader") {
              type = "activity_one_leader";
            }
          } else {
            // I am not in the activity, I see the students who are not in the activity
            students = selfGroup.users
              .flatMap((user) => user.sessions)
              .filter((session) => !activityData.confirmedSessions.includes(session))
              .map((s) => ({ session: s, audio: true, video: true }));
          }
        } else {
          // No activity ongoing, i see my whole group
          students = selfGroup.users.flatMap((user) => user.sessions).map((s) => ({ session: s, audio: true, video: true }));
        }
      }
    } else if (areGroupsActive) {
      // Not ongoing but active, preparing
      if (selfGroup) {
        // I picked a group already
        type = "group";
        students = selfGroup.users.flatMap((user) => user.sessions).map((s) => ({ session: s, audio: true, video: true }));
      } else {
        // I am still picking, i can hear experts
        experts = expertUsers.flatMap((expert) => expert.sessions).map((s) => ({ session: s, audio: true, video: false }));
        // And i can hear students who did not pick yet
        students = studentUsers
          .filter((user) => !user.group)
          .flatMap((user) => user.sessions)
          .map((s) => ({ session: s, audio: true, video: false }));
      }
    } else {
      // No group ongoing or active, i see all experts (and hear all students)
      // the conversation mode is default, "normal"
      students = studentUsers.flatMap((student) => student.sessions).map((s) => ({ session: s, audio: true, video: false }));
      if (activityData?.mode !== "pedagogy") {
        // Maybe the experts are busy with the pedagogy
        experts = expertUsers.flatMap((expert) => expert.sessions).map((s) => ({ session: s, audio: true, video: true }));
      }
    }

    // Default available view
    views.push({
      type,
      students,
      experts,
      selfGroup,
    });

    return views;
  }, [populatedData, userId, activityData, sessionId]);

export const getExpertView = (
  type: ExpertView["type"],
  populatedData: PopulatedMeetingData | undefined,
  activityData: ActivityData | undefined,
  spiedGroupId: string | undefined
): ExpertView | undefined => {
  if (!populatedData) return undefined;

  const { rooms, expertUsers } = populatedData;
  let students: SessionEnabledStreamsData[] = [];
  let experts = expertUsers.flatMap((expert) => expert.sessions).map((s) => ({ session: s, audio: true, video: true }));
  let group: PopulatedMeetingRoom | undefined = undefined;

  if (type === "highlight") {
    const areGroupsActive = rooms.groups.some((group) => group.room.status !== MeetingRoomStatus.Closed);
    students = populatedData.studentUsers.flatMap((student) => student.sessions).map((s) => {
      const isConfirmed = !!activityData?.confirmedSessions.some((cs) => cs.id === s.id);
      return { 
        session: s, 
        audio: isConfirmed || !areGroupsActive, // we can hear all students, except in group mode
        video: isConfirmed,
      };
    });
  } else if (type === "groups_overview") {
    // Expert is not in an activity and not spying, he can't see/hear students
    students = [];
    experts = [];
  } else if (type === "activity_one_group" || type === "activity_one_leader" || type === "activity_all_leaders") {
    // Expert is in an activity
    if (activityData?.mode === "one_group") group = activityData.group;
    if (activityData?.mode === "one_leader") group = activityData.group;
    students = activityData?.confirmedSessions.map((s) => ({ session: s, audio: true, video: true })) ?? [];
  } else if (type === "activity_one_group_spy") {
    // Not in an activity but spying
    const spiedGroup = rooms.groups.find((group) => group.id === spiedGroupId);
    if (spiedGroup) {
      students = spiedGroup.users.flatMap((user) => user.sessions).map((s) => ({ session: s, audio: true, video: true }));
      group = spiedGroup;
    }
  } else if (type === "groups_enlistment") {
    // Groups are not ongoing but preparing, expert can see the students
    // who did not pick groups yet
    students = populatedData.studentUsers
      .filter((user) => !user.group)
      .flatMap((user) => user.sessions)
      .map((s) => ({ session: s, audio: true, video: false }));
  } else if (type === "normal") {
    // Normal mode, expert can see and hear all students
    students = populatedData.studentUsers.flatMap((student) => student.sessions).map((s) => ({ session: s, audio: true, video: true }));
  }

  // only one forced view for the expert, for now
  experts.forEach((expert) => {
    if (expert.session.session.canBeHeardByEveryone) {
      expert.audio = true;
    }
  });

  return {
    type,
    students,
    experts,
    group,
  };
};

export const getCurrentExpertGroupView = (
  populatedData: PopulatedMeetingData | undefined,
  activityData: ActivityData | undefined,
  spiedGroupId: string | undefined
): ExpertView | undefined => {
  if (!populatedData) return undefined;
  const { rooms } = populatedData;
  const areGroupsOngoing = rooms.groups.some((group) => group.room.status === MeetingRoomStatus.Ongoing);
  let type: ExpertView["type"] | undefined = undefined;
  if (areGroupsOngoing) {
    type = "groups_overview";
    // By default, expert can't see/hear students in groups
    if (activityData) {
      if (activityData.mode === "one_group") {
        type = "activity_one_group";
      } else if (activityData.mode === "one_leader") {
        type = "activity_one_leader";
      } else if (activityData.mode === "all_leaders") {
        type = "activity_all_leaders";
      }
    } else if (spiedGroupId) {
      type = "activity_one_group_spy";
    }
  }

  return type ? getExpertView(type, populatedData, activityData, spiedGroupId) : undefined;
};

export const useExpertAvailableViews = (
  populatedData: PopulatedMeetingData | undefined,
  activityData: ActivityData | undefined,
  spiedGroupId: string | undefined
): ExpertView[] =>
  useMemo<ExpertView[]>(() => {
    if (!populatedData) return [];

    const { rooms } = populatedData;

    let type: ExpertView["type"] = "normal";
    const areGroupsOngoing = rooms.groups.some((group) => group.room.status === MeetingRoomStatus.Ongoing);
    const areGroupsActive = rooms.groups.some((group) => group.room.status !== MeetingRoomStatus.Closed);

    if (activityData?.mode === "pedagogy") {
      type = "activity_pedagogy";
    } else if (activityData?.mode === "highlight") {
      type = "highlight";
    } else if (areGroupsOngoing) {
      type = "groups_overview";
      // By default, expert can't see/hear students in groups
      if (activityData) {
        if (activityData.mode === "one_group") {
          type = "activity_one_group";
        } else if (activityData.mode === "one_leader") {
          type = "activity_one_leader";
        } else if (activityData.mode === "all_leaders") {
          type = "activity_all_leaders";
        }
      } else if (spiedGroupId) {
        type = "activity_one_group_spy";
      }
    } else if (areGroupsActive) {
      type = "groups_enlistment";
    }

    const view = getExpertView(type, populatedData, activityData, spiedGroupId);
    if (!view) return [];
    return [view];
  }, [populatedData, activityData, spiedGroupId]);

export const usePedagogyAvailableViews = (populatedData: PopulatedMeetingData | undefined, userId: string | undefined): PedagogyView[] =>
  useMemo<PedagogyView[]>(() => {
    if (!populatedData || !userId) return [];

    const { rooms } = populatedData;

    const views: PedagogyView[] = [];

    (window as any).views = views;

    let type: PedagogyView["type"] = "normal";
    let students: SessionEnabledStreamsData[] = [];
    let experts: SessionEnabledStreamsData[] = [];

    const group = rooms.groups.find((group) => (group.room.enlistedUserIds ?? []).includes(userId) && group.room.status !== MeetingRoomStatus.Closed);

    if (group) {
      type = "communication";
      // peda room contains both users and peda
      group.users
        .flatMap((u) => u.sessions)
        .forEach((session) => {
          if (isMeetingSessionRoleGreaterThanOrEqual(session.session.role, MeetingSessionRole.MODERATOR)) {
            experts.push({ session, audio: true, video: true });
          } else {
            students.push({ session, audio: true, video: true });
          }
        });
      views.push({ type, students, experts, group });
      return views;
    }

    // Default available view
    views.push({
      type,
      students,
      experts,
    });

    return views;
  }, [populatedData, userId]);
